import { css, html, nothing, LitElement } from 'lit';
import { customElement } from 'lit/decorators.js';
import {
   enumAttributeConverter,
   Sizes,
   SizesValuesMap,
   States,
   StatesValuesMap,
   Variant,
   VariantValuesMap,
} from '../enum-types.ts';
import { getGlobalStyleSheets } from '../../global-styles';
import { AtlChipProps } from './chip.props.ts';
import { ifDefined } from 'lit/directives/if-defined.js';

import * as TagNames from './chip.tagnames.ts';

@customElement(TagNames.Chip)
export class AtlChip extends LitElement implements AtlChipProps {
   declare label?: string;

   declare variant?: Variant;
   declare reverseOrder?: boolean;
   declare size?: Sizes;
   declare state?: States;

   declare avatarIconName?: string;
   declare avatarClassName?: string;

   declare avatarPicturUrl?: string;
   declare avatarPictureAltText?: string;

   declare hiddenLabel?: string;

   declare iconName?: string;
   declare iconClassName?: string;

   declare clickable?: boolean;

   declare deletable?: boolean;

   static properties = {
      label: { type: String },
      variant: { type: String, converter: enumAttributeConverter(Variant) },
      reverseOrder: { attribute: 'reverse-order', type: Boolean },
      size: { type: String, converter: enumAttributeConverter(Sizes) },
      state: { type: String, converter: enumAttributeConverter(States) },
      avatarIconName: { attribute: 'avatar-icon-name', type: String },
      avatarClassName: { attribute: 'avatar-class-name', type: String },
      avatarPicturUrl: { attribute: 'avatar-picture-url', type: String },
      avatarPictureAltText: {
         attribute: 'avatar-picture-alt-text',
         type: String,
      },
      hiddenLabel: { attribute: 'hidden-label', type: String },
      iconName: { attribute: 'icon-name', type: String },
      iconClassName: { attribute: 'icon-class-name', type: String },
      clickable: { type: Boolean },
      deletable: { type: Boolean },
   };

   static styles = [
      ...getGlobalStyleSheets(),
      css`
         :host-context(.flex) span.chip {
            width: 100%;
         }
      `,
   ];

   renderAvatarIcon() {
      if (!this.avatarIconName) return nothing;

      const classNames = [
         'avatar-icon',
         'fa-solid',
         `fa-${this.avatarIconName}`,
      ];
      if (this.avatarClassName) {
         classNames.push(this.avatarClassName);
      }
      if (this.size) {
         classNames.push(SizesValuesMap[this.size]);
      }

      return html`<i class="${classNames.join(' ')}"></i>`;
   }

   renderAvatarPicture() {
      const classNames = ['avatar-icon'];
      if (this.avatarClassName) {
         classNames.push(this.avatarClassName);
      }
      if (this.size) {
         classNames.push(SizesValuesMap[this.size]);
      }

      if (this.avatarPicturUrl) {
         return html`<img
            src="${this.avatarPicturUrl}"
            alt=${ifDefined(this.avatarPictureAltText)}
            class="${classNames.join(' ')}"
         />`;
      }
      return nothing;
   }

   renderHiddenLabel() {
      if (this.hiddenLabel) {
         return html`<span class="visually-hidden">${this.hiddenLabel}</span>`;
      }
      return nothing;
   }

   renderIcon() {
      let theIconName;
      if (this.iconName && !this.deletable) {
         theIconName = this.iconName;
      } else if (this.deletable) {
         theIconName = 'xmark';
      }

      if (theIconName) {
         const classNames: string[] = [
            'chip-icon',
            'fa-solid',
            `fa-${theIconName}`,
         ];
         if (this.iconClassName) {
            classNames.push(this.iconClassName);
         }

         if (this.deletable) {
            classNames.push('delete-icon');
         }

         return html`<i class="${classNames.join(' ')}"></i>`;
      }
      return nothing;
   }

   render() {
      const spanClasses = ['chip'];
      if (this.variant) {
         spanClasses.push(VariantValuesMap[this.variant]);
      }
      if (this.reverseOrder) {
         spanClasses.push('flex-row-reverse');
      }
      if (this.size) {
         spanClasses.push(SizesValuesMap[this.size]);
      }

      if (this.clickable) {
         spanClasses.push('clickable');
      }

      if (this.state) {
         spanClasses.push(StatesValuesMap[this.state]);
      }

      return html`
         <span class="${spanClasses.join(' ')}" part="chip">
            ${this.renderAvatarIcon()} ${this.renderAvatarPicture()}
            ${this.label} ${this.renderHiddenLabel()} ${this.renderIcon()}
         </span>
      `;
   }
}
